<script lang="ts">
    import {Button, Input, Label, Modal, Select, Spinner} from "flowbite-svelte";
    import type {Contract, Employee} from "$lib/types/employee";
    import dayjs from "dayjs";
    import {getContext, onMount} from "svelte";
    import {mailTemplates} from "$lib/stores/mailTemplates";
    import {getEmailTemplates, sendEmailTemplate} from "$lib/calls/mail";
    import {generateContractModification} from "$lib/utils/templating/generateContractPreview";
    import {DownloadOutline, EnvelopeSolid} from "flowbite-svelte-icons";
    import {formDataPost, post, retire} from "$lib/api";
    import type {EmailTemplate} from "$lib/types/EmailTemplate";
    import EmailTemplateEditor from "$lib/components/EmailTemplateEditor.svelte";
    import {allUnits} from "$lib/stores/unit";
    import {getUnits} from "$lib/calls/unit";
    import {findUnmatchedVariables} from "$lib/utils/templating/parseHandlebars";
    import {globalToast} from "$lib/stores/toast";
    import ModalHeader from "$lib/components/ui/ModalHeader.svelte";
    import ModalFooter from "$lib/components/ui/ModalFooter.svelte";
    import CardLayout from "$items/CardLayout.svelte";
    import VerticalGrid from "$lib/components/VerticalGrid.svelte";
    import {tariffs} from "$lib/stores/tariffs";
    import InfoBox from "$items/InfoBox.svelte";
    import type {Writable} from "svelte/store";
    export let open: boolean = false;
    export let contract: Contract;

    export let employee: Employee;

    let preview: Blob | null = null;

    let previewForm: HTMLFormElement;

    let doneModal:Writable<boolean> = getContext('doneModal');

    let changeData ={
        input: contract.currentHistory.workDaysPerWeek,
        when: dayjs().add(1, 'month').set('date', 1).format('YYYY-MM-DD')
    }
    let newHistory = {...contract.currentHistory, id:null, historyType: 'adjustment-hours'}

    let template: EmailTemplate;

    let loading = false;



    const createDocument = async () => {
        loading = true;
        newHistory = {...contract.history.find((x:any) => x.startDate.stamp < dayjs(changeData.when).valueOf() && x.endDate.stamp < dayjs(changeData.when).valueOf()), id:null}
        newHistory.workDaysPerWeek = changeData.input
        newHistory.historyType = 'adjustment-hours'
        preview = await generateContractModification(employee, contract, newHistory, 'adjustment-hours', {
            employmentPartTimeFullTime: contract.contractType === 'fulltime' ? 'Vollzeit' : 'Teilzeit',
            fillInDate: changeData.when
        })
        loading = false;
    }
    const changeContract = async () => {
        loading = true;
        try {
            const createdHistory = await post('/employee/' + employee.uuid + '/contract/' + contract.id + '/history', {...newHistory, startDate: changeData.when})
            const result = await formDataPost('/document/contract', {
                file: preview,
                connectedEntity: 'EmployeeContract',
                entityId: createdHistory.contractId,
                historyId: createdHistory.id
            })
            newHistory = result.contract.history.find((x:any) => x.id === createdHistory.id)
        } catch (e) {
            alert('Schwerer Fehler bei der Verarbeitung. Bitte IT informieren.')
            console.log(e)
        } finally {
            loading = false
        }
    }
    const sendEmail = async () => {
        loading = true;
        await sendEmailTemplate(template.id, {
            fromEmail: 'legal@recrew.de',
            fromName: 'REcrew Legal',
            subject: 'REcrew_Vertragsanpassung_zwischen_' + employee.name + ' und REcrew GmbH zur Signatur bereit!',
            entityId: employee.id,
            content: template.content.replace(/<% body %>/g, ''),
            toEmail: employee.email,
            toName: employee.name,
            variables: findUnmatchedVariables(template.content)
        })
        $doneModal = true
        open = false
        loading = false;
    }
    const revert = async () => {
        loading = true;
        preview = null;
        if(newHistory.id){
            await retire(`/employee/${employee.uuid}/contract/${contract.id}/history/${newHistory.id}?silent=true`)
        }
        loading = false;
    }
    onMount(async () => {
        if($mailTemplates.length === 0) {
            $mailTemplates = await getEmailTemplates();
        }
        if($allUnits.length === 0) {
            $allUnits = await getUnits();
        }
        template = $mailTemplates.find(x => x.level === 'active' && x.name.toLowerCase().includes('stundenkontingent_sent'))
    })

</script>

<Modal size="lg" bind:open title="Stundenanpassung">
    <svelte:fragment slot="header">
        <ModalHeader>Stundenanpassung</ModalHeader>
    </svelte:fragment>
    {#if newHistory.id === null}
        <CardLayout title="Aktueller Vertrag">
            <VerticalGrid>
                <InfoBox title="Aktuelle Arbeitstage" value="{contract.currentHistory.workDaysPerWeek} Tage/Woche" />
                <InfoBox title="Aktuelle Arbeitsstunden" value="{(contract.currentHistory.workHoursPerDay * contract.currentHistory.workDaysPerWeek * 4.3).toFixed(0)} Stunden/Monat" />
                <svelte:fragment slot="right">
                    <InfoBox title="Neue Arbeitstage" value="{changeData.input} Tage/Woche" />
                    <InfoBox title="Neue Arbeitsstunden" value="{(contract.currentHistory.workHoursPerDay * changeData.input * 4.3).toFixed(0)} Stunden/Monat" />
                </svelte:fragment>
            </VerticalGrid>
        </CardLayout>
        <form bind:this={previewForm} class="flex gap-3" on:submit|preventDefault={() => changeContract()}>

            <Label class="space-y-2 flex-1">
                <span>Arbeitstage pro Woche</span>
                <Input disabled={!!preview} required step="0.5" type="number" bind:value={changeData.input}/>
            </Label>
            <Label class="space-y-2">
                <span>Zum</span>
                <Input disabled={!!preview} required type="date" bind:value={changeData.when}/>
            </Label>

        </form>

    {:else}
        <form class="space-y-3" on:submit={sendEmail}>
            <div class="space-y-2">
                <span>Email Template</span>
                <Select required bind:value={template.id} on:change={(e) => {const id = template.id; template = null; setTimeout(() => {template = $mailTemplates.find(x => x.id === id)})}} items={$mailTemplates.filter(x => x.level === 'active').map(x => ({...x, value: x.id}))}/>
            </div>
            {#if template}
                <EmailTemplateEditor on:update={e => template = e.detail} preview={true} template={template} fillData={{Employee: employee, Contract: newHistory, Unit: $allUnits.find(x => x.id === employee.unitId)}} />
            {/if}
        </form>
    {/if}
    <svelte:fragment slot="footer">
        <ModalFooter showEdit={!!preview} disabled={loading} on:abort={() => {revert(); open = false}} on:edit={() => revert()}>
            <svelte:fragment slot="right">
                {#if preview}
                    <Button class="!text-decoration-none whitespace-nowrap" download="{employee.name.replaceAll(' ','_')}.docx" href={URL.createObjectURL(preview)} target="_blank" color="light">
                        <DownloadOutline/> Vorschau
                    </Button>
                    {#if newHistory.id === null}
                        <Button on:click={() => previewForm.requestSubmit()} class="bg-sky-100 text-neutral-600 whitespace-nowrap"><EnvelopeSolid class="w-4 mr-2"/>Vertragsanpassung durchführen</Button>
                    {:else}
                        <Button on:click={() => sendEmail()} class="bg-sky-100 text-neutral-600 whitespace-nowrap"><EnvelopeSolid class="w-4 mr-2"/>E-Mail senden</Button>
                    {/if}
                {:else}
                    <Button on:click={() => createDocument()} class="bg-sky-100 text-neutral-600 whitespace-nowrap"><EnvelopeSolid class="w-4 mr-2"/>Vorschau erzeugen</Button>
                {/if}
            </svelte:fragment>
        </ModalFooter>
    </svelte:fragment>
</Modal>